import { supabase, run } from './db';

/**
 * Trilha de auditoria.
 *
 * Sem `cached`: log é para investigar, e mostrar cópia local de uma trilha
 * seria a pior forma de enganar quem audita — a pessoa concluiria que algo
 * não aconteceu porque a cópia era velha. Sem rede, o certo é falhar.
 */

/** Tabelas auditadas, com nome de gente. A ordem é a do filtro na tela. */
export const TABELAS_AUDITADAS = {
  services: 'Serviços',
  clients: 'Clientes',
  invoices: 'Faturas',
  invoice_installments: 'Parcelas',
  lab_members: 'Equipe',
  technicians: 'Técnicos',
  laboratories: 'Laboratório',
};

export const ACOES = {
  INSERT: 'Criou',
  UPDATE: 'Alterou',
  DELETE: 'Excluiu',
};

/**
 * Página da trilha, mais recente primeiro.
 *
 * Paginado porque a trilha só cresce: sem limite, a tela ficaria mais lenta a
 * cada semana de uso até parar de abrir.
 */
export const listarAuditoria = ({ laboratoryId, tabela, acao, autor, desde, pagina = 0, porPagina = 50 }) => {
  let q = supabase
    .from('audit_log')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('at', { ascending: false })
    .range(pagina * porPagina, pagina * porPagina + porPagina - 1);

  if (tabela) q = q.eq('table_name', tabela);
  if (acao) q = q.eq('action', acao);
  if (autor) q = q.eq('actor_id', autor);
  if (desde) q = q.gte('at', desde);

  return run(q);
};

/** Campos que não dizem nada a quem lê a trilha. */
const RUIDO = new Set([
  'id', 'created_at', 'updated_at', 'laboratory_id', 'user_id',
  'numero', 'global_number',
]);

/**
 * As mudanças de um registro, prontas para a tela.
 *
 * Devolve lista em vez do objeto cru porque a ordem importa na leitura, e
 * porque o `de`/`para` cru traz `null` e aspas de JSON no meio do texto.
 */
export const mudancasLegiveis = (changes) => {
  if (!changes) return [];
  return Object.entries(changes)
    .filter(([campo]) => !RUIDO.has(campo))
    .map(([campo, { de, para }]) => ({
      campo,
      de: valorLegivel(de),
      para: valorLegivel(para),
    }));
};

/** Nulo vira "vazio"; booleano vira sim/não; o resto vira texto. */
export const valorLegivel = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'sim';
  if (v === false) return 'não';
  if (Array.isArray(v)) return v.join(', ') || '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Como identificar a linha que foi mexida.
 *
 * O id cru não diz nada a quem audita. Cada tabela tem o seu jeito de se
 * apresentar, e o snapshot do DELETE é a única fonte quando a linha já não
 * existe — é justamente o caso em que a pergunta é mais urgente.
 */
export const identificaLinha = (registro) => {
  const dados = registro.snapshot || {};
  switch (registro.table_name) {
    case 'services':
      return dados.service_code
        ? `#${dados.service_code}${dados.client_name ? ` · ${dados.client_name}` : ''}`
        : dados.client_name || registro.row_id?.slice(0, 8);
    case 'clients':
    case 'technicians':
    case 'laboratories':
      return dados.name || registro.row_id?.slice(0, 8);
    case 'invoices':
      return dados.numero_exibicao ? `Fatura #${dados.numero_exibicao}` : registro.row_id?.slice(0, 8);
    default:
      return registro.row_id?.slice(0, 8) || '—';
  }
};

/**
 * PARA ONDE ESTE REGISTRO LEVA.
 *
 * A trilha dizia o que mudou e deixava a pessoa procurar onde. "Alterou
 * Serviços #2026-0047 · te_done" e então era trocar de aba, achar o serviço na
 * lista, abrir a porta certa e caçar o campo — quatro passos para conferir uma
 * linha que a própria trilha acabou de nomear.
 *
 * O destino sai do CAMPO QUE MUDOU, porque é ele que diz onde aquilo se edita:
 *
 *   etapa do workflow   dado de execução — a data real, o "concluído", as
 *                       quantidades, as observações daquela etapa;
 *   financeiro          o documento e a baixa: faturou, valor faturado, pagou;
 *   cadastro            tudo que define o serviço antes de ele acontecer —
 *                       cliente, etapas contratadas, preços, e a AGENDA. Data
 *                       agendada é cadastro, data real é etapa: parecem a mesma
 *                       coisa e se editam em telas diferentes.
 *
 * DELETE não tem destino, e isso é resposta: a linha não existe mais, e abrir
 * um serviço excluído mostraria ou uma tela vazia ou — pior — outra coisa que
 * tenha ficado no lugar. O conteúdo do que foi excluído continua no snapshot,
 * que é o que a trilha tem de melhor a oferecer ali.
 */

/** Campos que se editam no cadastro, apesar de parecerem de outra porta. */
const CAMPOS_DO_CADASTRO_NA_TRILHA = new Set([
  // O ponteiro do `sem_cobranca`: quem fatura este serviço é escolhido no
  // cadastro, não na tela de dinheiro.
  'billing_service_id',
  'billing_model',
  // A etapa herdada e a sua origem: vínculo de cadastro.
  'source_stage', 'source_service_id',
]);

/** Campos do documento e da baixa. */
const CAMPOS_DO_DINHEIRO = new Set([
  'billed', 'billed_value', 'billed_date', 'billing_notes', 'observacao_de_cobranca',
  'paid', 'paid_at', 'valor_pago', 'valor_total', 'vencimento', 'paga_em',
]);

/**
 * De que etapa é o campo, pelo prefixo da coluna.
 *
 * Os ids são os das ABAS do workflow, e não os da corrente: DG30 e DG60 dividem
 * a aba de Prenhez, que é onde as duas se lançam.
 */
const ETAPA_DO_PREFIXO = [
  ['protocolo_', 'protocolo'],
  ['opu_', 'opu'],
  ['lab_', 'fiv'],
  ['te_', 'te'],
  // O destino dos embriões transferidos é lançado junto da TE.
  ['dest_', 'te'],
  ['pregnancy_', 'pregnancy'],
];

/** Campos de etapa cujo nome não carrega o prefixo dela. */
const ETAPA_DO_CAMPO = {
  // Vitrificados e DT são resultado da FIV: entram na mesma soma que os
  // estágios, e é na FIV que se lançam.
  qtd_vitrificados: 'fiv',
  qtd_dt: 'fiv',
  qtd_desvitrificados: 'te',
  qtd_descartados: 'fiv',
  // A entrada é congelada no lançamento da TE — taxa e quantidade saem de lá,
  // e é lá que se corrigem.
  entrada_por_embriao: 'te',
  entrada_embrioes: 'te',
  pregnancy_data: 'pregnancy',
};

/** A que porta um campo de `services` pertence. */
export const portaDoCampo = (campo) => {
  if (!campo) return 'cadastro';
  // A AGENDA É CADASTRO. Vem antes do prefixo de propósito: `te_scheduled_date`
  // casaria com `te_` e mandaria a pessoa para a aba da TE, que não é onde a
  // data prevista se muda.
  if (campo.endsWith('_scheduled_date')) return 'cadastro';
  if (CAMPOS_DO_CADASTRO_NA_TRILHA.has(campo)) return 'cadastro';
  if (CAMPOS_DO_DINHEIRO.has(campo)) return 'financeiro';
  if (ETAPA_DO_CAMPO[campo]) return 'etapas';
  return ETAPA_DO_PREFIXO.some(([p]) => campo.startsWith(p)) ? 'etapas' : 'cadastro';
};

/** A aba do workflow de um campo de etapa, ou nulo. */
export const etapaDoCampo = (campo) => {
  if (!campo || campo.endsWith('_scheduled_date')) return null;
  if (ETAPA_DO_CAMPO[campo]) return ETAPA_DO_CAMPO[campo];
  const achado = ETAPA_DO_PREFIXO.find(([p]) => campo.startsWith(p));
  return achado ? achado[1] : null;
};

// A ordem da corrente, para desempatar. O workflow grava todas as abas de uma
// vez, então uma alteração pode tocar OPU e TE na mesma linha da trilha; ganha a
// etapa com mais campos mexidos e, no empate, a mais ADIANTE — a corrente anda
// para frente, e a etapa mais recente é a que acabou de acontecer.
const ORDEM_DA_ETAPA = ['protocolo', 'opu', 'fiv', 'te', 'pregnancy'];

const ROTULO_DA_PORTA = {
  etapas: 'Abrir etapa',
  financeiro: 'Abrir financeiro',
  cadastro: 'Abrir cadastro',
  cliente: 'Abrir cliente',
};

export const destinoDoRegistro = (
  registro,
  // De qual serviço é cada fatura e cada parcela. Vêm de quem já carregou as
  // faturas, e não do snapshot: O SNAPSHOT NÃO EXISTE NO UPDATE. Em produção são
  // 4 alterações de fatura e toda baixa de parcela — justamente as linhas que se
  // quer abrir —, e todas elas ficariam sem link se o caminho fosse por ele.
  { servicoDaFatura = {}, servicoDaParcela = {} } = {},
) => {
  if (!registro || registro.action === 'DELETE') return null;

  const snapshot = registro.snapshot || {};
  const campos = Object.keys(registro.changes || {});

  switch (registro.table_name) {
    case 'services': {
      if (!registro.row_id) return null;

      // Serviço recém-criado não tem campo alterado: o destino é o cadastro,
      // que é o que acabou de ser preenchido.
      const portas = campos.map(portaDoCampo);
      const porta = portas.includes('etapas') ? 'etapas'
        : portas.includes('financeiro') ? 'financeiro'
        : 'cadastro';

      let etapa = null;
      if (porta === 'etapas') {
        const contagem = {};
        campos.forEach(c => {
          const e = etapaDoCampo(c);
          if (e) contagem[e] = (contagem[e] || 0) + 1;
        });
        etapa = Object.keys(contagem).sort((a, b) =>
          (contagem[b] - contagem[a])
          || (ORDEM_DA_ETAPA.indexOf(b) - ORDEM_DA_ETAPA.indexOf(a)))[0] || null;
      }

      return { porta, rotulo: ROTULO_DA_PORTA[porta], serviceId: registro.row_id, etapa };
    }

    case 'invoices': {
      // O mapa primeiro; o snapshot é a reserva do INSERT e do DELETE, e a única
      // fonte de uma fatura cancelada, que não está mais na lista em memória.
      const serviceId = servicoDaFatura[registro.row_id] || snapshot.service_id;
      if (!serviceId) return null;
      return { porta: 'financeiro', rotulo: ROTULO_DA_PORTA.financeiro, serviceId, etapa: null };
    }

    case 'invoice_installments': {
      // A parcela não sabe de que serviço é: ela aponta para a fatura, e é a
      // fatura que aponta para o serviço. Dois caminhos até ele, e o segundo só
      // serve ao INSERT, que é quando existe snapshot.
      const serviceId = servicoDaParcela[registro.row_id]
        || servicoDaFatura[snapshot.invoice_id];
      if (!serviceId) return null;
      return { porta: 'financeiro', rotulo: ROTULO_DA_PORTA.financeiro, serviceId, etapa: null };
    }

    case 'clients': {
      if (!registro.row_id) return null;
      return { porta: 'cliente', rotulo: ROTULO_DA_PORTA.cliente, clientId: registro.row_id, etapa: null };
    }

    // Equipe, técnicos e laboratório se editam na aba Laboratório, que não abre
    // numa linha: não há o que linkar sem inventar uma navegação nova.
    default:
      return null;
  }
};
